<script setup>
import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import AgentPauseSessionsAPI from 'dashboard/api/agentPauseSessions';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserID = useMapGetter('getCurrentUserID');
const activePauseTypes = useMapGetter('agentPauseTypes/activePauseTypes');

const { isImpersonating } = useImpersonation();

const activePauseSession = ref(null);
const remainingSeconds = ref(0);
let countdownInterval = null;

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  remainingSeconds.value = 0;
}

function startCountdown(endsAt) {
  stopCountdown();
  const endTime = new Date(endsAt).getTime();
  function tick() {
    const diff = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
    remainingSeconds.value = diff;
    if (diff === 0) stopCountdown();
  }
  tick();
  countdownInterval = setInterval(tick, 1000);
}

function formatCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

async function refreshActivePauseSession() {
  try {
    const { data } = await AgentPauseSessionsAPI.get();
    const sessions = Array.isArray(data) ? data : [];
    const session = sessions.find(s => s.user_id === currentUserID.value);
    if (session) {
      activePauseSession.value = {
        id: session.pause_type_id,
        name: session.pause_type_name,
        ends_at: session.ends_at,
      };
      startCountdown(session.ends_at);
    } else {
      activePauseSession.value = null;
      stopCountdown();
    }
  } catch (_e) {
    activePauseSession.value = null;
    stopCountdown();
  }
}

onMounted(() => {
  store.dispatch('agentPauseTypes/fetch');
  refreshActivePauseSession();
});

onUnmounted(() => {
  stopCountdown();
});

watch(currentUserAvailability, value => {
  if (value === 'paused') {
    if (!activePauseSession.value) refreshActivePauseSession();
  } else {
    activePauseSession.value = null;
    stopCountdown();
  }
});

const availabilityStatuses = computed(() => [
  {
    label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    value: 'online',
    color: 'bg-n-teal-9',
    icon: h('span', { class: 'bg-n-teal-9 size-[12px] rounded' }),
    active:
      !activePauseSession.value && currentUserAvailability.value === 'online',
  },
]);

const activeStatus = computed(() => {
  if (activePauseSession.value) {
    const countdown =
      remainingSeconds.value > 0
        ? ` (${formatCountdown(remainingSeconds.value)})`
        : '';
    return {
      label: `${activePauseSession.value.name}${countdown}`,
      color: 'bg-n-amber-9',
    };
  }
  return {
    label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    color: 'bg-n-teal-9',
  };
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

async function startPause(pauseType) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    const { data } = await AgentPauseSessionsAPI.start(pauseType.id);
    activePauseSession.value = {
      id: data.pause_type_id,
      name: data.pause_type_name,
      ends_at: data.ends_at,
    };
    startCountdown(data.ends_at);
  } catch (error) {
    useAlert(t('AGENT_PAUSE_TYPES.API.ERROR'));
  }
}

async function endPause() {
  try {
    await AgentPauseSessionsAPI.end();
    activePauseSession.value = null;
    stopCountdown();
  } catch (error) {
    useAlert(t('AGENT_PAUSE_TYPES.API.ERROR'));
  }
}
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <div class="grid gap-0">
      <DropdownItem preserve-open class="gap-1">
        <div class="flex-grow flex items-center gap-1 min-w-0">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center min-w-0 text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span class="truncate max-w-[7rem]">
                  {{ activeStatus.label }}
                </span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20" max-height="min(60vh, 20rem)">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
            <template v-if="activePauseTypes.length">
              <hr class="border-n-weak my-1" />
              <DropdownItem
                v-if="activePauseSession"
                :label="$t('SIDEBAR.END_PAUSE')"
                :icon="h('span', { class: 'bg-n-ruby-9 size-[12px] rounded' })"
                class="cursor-pointer"
                @click="endPause"
              />
              <template v-else>
                <DropdownItem
                  v-for="pauseType in activePauseTypes"
                  :key="pauseType.id"
                  :label="pauseType.name"
                  :icon="
                    h('span', { class: 'bg-n-amber-9 size-[12px] rounded' })
                  "
                  class="cursor-pointer"
                  @click="startPause(pauseType)"
                />
              </template>
            </template>
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
